import React, { useState } from 'react';
import { createPortal } from 'react-dom';

export type RationaleModalProps = {
  open: boolean;
  title?: string;
  description?: string;
  placeholder?: string;
  maxLength?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (rationale: string) => void;
  onCancel: () => void;
};

export function RationaleModal({
  open,
  title = 'Provide a Rationale',
  description,
  placeholder = 'Enter rationale…',
  maxLength = 500,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}: RationaleModalProps) {
  const [value, setValue] = useState('');

  if (!open) return null;

  const handleConfirm = () => {
    if (!value.trim()) return;
    onConfirm(value.trim());
    setValue('');
  };

  const handleCancel = () => {
    setValue('');
    onCancel();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60"
      role="presentation"
      onClick={handleCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rationale-modal-title"
        className="w-full max-w-md rounded-xl border border-gray-600 bg-gray-800 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 space-y-2">
          <h2 id="rationale-modal-title" className="text-lg font-semibold text-white">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-gray-400">{description}</p>
          )}
          <textarea
            autoFocus
            value={value}
            onChange={e => setValue(e.target.value)}
            maxLength={maxLength}
            rows={4}
            placeholder={placeholder}
            className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 resize-none focus:outline-none focus:border-cyan-500"
          />
          <span className="block text-right text-[10px] text-gray-600">
            {value.length}/{maxLength}
          </span>
        </div>
        <div className="flex justify-end gap-3 px-5 pb-5 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-500 text-gray-200 hover:bg-gray-700 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={!value.trim()}
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-700 hover:bg-amber-600 text-white border border-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
